import { Link, useLocation, Outlet } from 'react-router-dom';
import { useState } from 'react';
import { Menu, X, Globe } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { CookieConsent } from '../CookieConsent';

const T = {
  holdingLink: { de: 'Basic United Holding', en: 'Basic United Holding' },
  partOf: { de: 'Ein Unternehmen der Basic United Holding GmbH', en: 'A subsidiary of Basic United Holding GmbH' },
  nav: { de: 'Navigation', en: 'Navigation' },
  contact: { de: 'Kontakt', en: 'Contact' },
  rights: { de: 'Alle Rechte vorbehalten.', en: 'All rights reserved.' },
  imprint: { de: 'Impressum', en: 'Imprint' },
  privacy: { de: 'Datenschutz', en: 'Privacy Policy' },
};

export function SubsidiaryLayout({ config }) {
  return (
    <div className="min-h-screen flex flex-col">
      <SubNav config={config} />
      <div className="flex-1">
        <Outlet />
      </div>
      <SubFooter config={config} />
      <CookieConsent />
    </div>
  );
}

function SubNav({ config }) {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { language, toggleLanguage } = useLanguage();

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100" data-testid={`subnav-${config.id}`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-20">
          <Link to={config.basePath} className="flex items-center gap-3">
            <img src={config.logo} alt={config.name} className="h-10 md:h-12 w-auto object-contain" />
          </Link>

          <div className="hidden md:flex items-center gap-10">
            {config.navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`text-sm uppercase tracking-[0.15em] font-semibold transition-colors ${
                  pathname === link.href
                    ? `text-[${config.accentColor}] border-b-2 border-[${config.accentColor}] pb-1`
                    : 'text-[#1A1A1A] hover:text-[#F5A623]'
                }`}
              >
                {typeof link.label === 'object' ? link.label[language] : link.label}
              </Link>
            ))}
            <Link
              to="/unternehmen"
              className="text-xs uppercase tracking-[0.15em] text-[#718096] hover:text-[#0F1A2E] transition-colors border-l border-gray-200 pl-6 ml-2"
            >
              {T.holdingLink[language]}
            </Link>
            <button
              onClick={toggleLanguage}
              data-testid="sub-language-switcher"
              className="flex items-center gap-2 px-3 py-2 rounded-full border border-[#0F1A2E]/20 hover:border-[#F5A623] hover:text-[#F5A623] transition-colors text-sm font-medium"
              title={language === 'de' ? 'Switch to English' : 'Auf Deutsch wechseln'}
            >
              <Globe size={16} />
              <span>{language === 'de' ? 'EN' : 'DE'}</span>
            </button>
          </div>

          <button onClick={() => setOpen(!open)} className="md:hidden p-2" data-testid="sub-mobile-menu-toggle">
            {open ? <X size={24} className="text-[#1A1A1A]" /> : <Menu size={24} className="text-[#1A1A1A]" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden bg-white border-t border-gray-100 py-6 px-6">
          {config.navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              onClick={() => setOpen(false)}
              className="block py-3 text-sm uppercase tracking-[0.15em] font-semibold text-[#1A1A1A] hover:text-[#F5A623]"
            >
              {typeof link.label === 'object' ? link.label[language] : link.label}
            </Link>
          ))}
          <div className="border-t border-gray-100 mt-4 pt-4 space-y-3">
            <Link to="/unternehmen" onClick={() => setOpen(false)} className="block py-3 text-xs uppercase tracking-[0.15em] text-[#718096]">
              {T.holdingLink[language]}
            </Link>
            <button
              onClick={toggleLanguage}
              data-testid="sub-mobile-language-switcher"
              className="flex items-center gap-2 py-3 text-[#0F1A2E] hover:text-[#F5A623] transition-colors font-medium"
            >
              <Globe size={18} />
              <span>{language === 'de' ? 'Switch to English' : 'Auf Deutsch wechseln'}</span>
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}

function SubFooter({ config }) {
  const { language } = useLanguage();

  return (
    <footer className="bg-[#0A1628] text-white" data-testid={`subfooter-${config.id}`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
        <div className="grid md:grid-cols-3 gap-12">
          <div>
            <img src={config.logo} alt={config.name} className="h-10 w-auto brightness-0 invert mb-6" />
            <p className="text-white/60 text-sm leading-relaxed mb-4">
              {typeof config.tagline === 'object' ? config.tagline[language] : config.tagline}
            </p>
            <p className="text-white/40 text-xs">{T.partOf[language]}</p>
          </div>

          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-white/90 mb-6">{T.nav[language]}</h4>
            <ul className="space-y-3">
              {config.navLinks.map((link) => (
                <li key={link.href}>
                  <Link to={link.href} className="text-white/60 hover:text-[#F5A623] transition-colors text-sm">
                    {typeof link.label === 'object' ? link.label[language] : link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-white/90 mb-6">{T.contact[language]}</h4>
            <div className="space-y-3 text-sm text-white/60">
              <p>{config.name}</p>
              {config.address && <p>{config.address}</p>}
              <a href={`mailto:${config.email}`} className="block hover:text-[#F5A623] transition-colors">{config.email}</a>
              <a href={config.website} target="_blank" rel="noopener noreferrer" className="block hover:text-[#F5A623] transition-colors">{config.website}</a>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/40 text-xs">
            &copy; {new Date().getFullYear()} {config.name}. {T.rights[language]}
          </p>
          <div className="flex gap-6 text-xs text-white/40">
            <Link to="/impressum" className="hover:text-white/70">{T.imprint[language]}</Link>
            <Link to="/datenschutz" className="hover:text-white/70">{T.privacy[language]}</Link>
            <Link to="/unternehmen" className="hover:text-white/70">{T.holdingLink[language]}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
